import { defineStore } from 'pinia';
import { computed, reactive, ref, watch } from 'vue';
import { useDevicePixelRatio } from '@vueuse/core';
import { defaultAppearance, displayColor, displayVisible, normalizeHex, readAppearance } from './appearance';
import type { DentalInstance } from './types';

const storageKey = 'dental-cloud-appearance-v1';
// Display preferences never mutate the saved annotation, registry or label data.
export const useDentalAppearanceStore = defineStore('dental-appearance', () => {
  let initial = defaultAppearance();
  try { initial = readAppearance(JSON.parse(localStorage.getItem(storageKey) || 'null')); } catch { /* corrupt or unavailable storage */ }
  const preferences = reactive(initial);
  const scope = ref('local');
  const visibility = computed({ get: () => preferences.sampleVisibility[scope.value] || 'individual',
    set: value => { preferences.sampleVisibility[scope.value] = value; } });
  const { pixelRatio } = useDevicePixelRatio();
  watch(preferences, value => {
    try { localStorage.setItem(storageKey, JSON.stringify(value)); } catch { /* keep working if storage is unavailable */ }
  }, { deep: true });
  const scoped = (row: DentalInstance) => ({ ...row, id: `${scope.value}:${row.id}` });
  const color = (row: DentalInstance) => displayColor(scoped(row), preferences);
  const visible = (row: DentalInstance) => displayVisible(scoped(row), { ...preferences, visibility: visibility.value });
  function setVisible(row: DentalInstance, flag: boolean, instances: DentalInstance[]) {
    if (visibility.value !== 'individual') {
      for (const instance of instances) preferences.visibleOverrides[scoped(instance).id] = visible(instance);
      visibility.value = 'individual';
    }
    preferences.visibleOverrides[scoped(row).id] = flag;
  }
  function setColor(row: DentalInstance, value: string) {
    const hex = normalizeHex(value);
    if (!hex) return false;
    preferences.customColors[scoped(row).id] = hex; preferences.palette = 'custom'; return true;
  }
  function reset() { Object.assign(preferences, defaultAppearance()); }
  return { preferences, scope, visibility, pixelRatio, color, visible, setVisible, setColor, reset };
});
